import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Star, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

// Numéro WhatsApp de TIRERA GROUP (format international sans + ni espaces)
const BUSINESS_WHATSAPP = "22392001010";

export const Stars = ({ value = 0, onRate, accent = "#B45309", size = 16, readOnly = false, idPrefix = "star" }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            data-testid={`${idPrefix}-${n}`}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            onClick={() => !readOnly && onRate?.(n)}
            className={readOnly ? "cursor-default" : "cursor-pointer"}
          >
            <Star size={size} fill={active ? accent : "none"} style={{ color: accent }} />
          </button>
        );
      })}
    </div>
  );
};

export const LeadModal = ({ open, config, accent = "#B45309", onClose }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Verrouille le défilement de la page en arrière-plan quand la fenêtre est ouverte (desktop + mobile)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!config) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const fld of config.fields) {
      if (fld.required && !form[fld.name]) {
        toast.error(`Le champ « ${fld.label} » est requis`);
        return;
      }
    }
    setSaving(true);
    try {
      await config.onSubmit(form);
      toast.success(config.success || "Demande envoyée. Nous vous recontacterons rapidement.");
      setForm({});
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue, réessayez.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-border bg-white px-4 py-2.5 text-navy placeholder-muted-foreground/60 outline-none focus:border-navy/40";

  const sendWhatsApp = () => {
    for (const fld of config.fields) {
      if (fld.required && !form[fld.name]) {
        toast.error(`Le champ « ${fld.label} » est requis`);
        return;
      }
    }
    const lines = config.fields
      .filter((fld) => form[fld.name])
      .map((fld) => `${fld.label} : ${form[fld.name]}`);
    const msg = `Bonjour TIRERA Construction & Habitat,\n\nDemande : ${config.subtitle || config.title}\n\n${lines.join("\n")}`;
    window.open(`https://wa.me/${BUSINESS_WHATSAPP}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] overflow-y-auto bg-navy/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid="lead-modal"
          data-lenis-prevent
        >
          <div className="flex min-h-full items-end justify-center p-0 sm:items-center sm:p-6">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
          >
            <div className="flex items-start justify-between gap-4 p-6 pb-2">
              <div>
                <h3 className="font-display text-xl font-semibold text-navy">{config.title}</h3>
                {config.subtitle && <p className="mt-1 text-sm text-muted-foreground">{config.subtitle}</p>}
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="lead-modal-close"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4 p-6 pt-4">
              {config.fields.map((fld) => (
                <label key={fld.name} className="block">
                  <span className="mb-1.5 block text-sm font-medium text-navy/80">
                    {fld.label} {fld.required && <span style={{ color: accent }}>*</span>}
                  </span>
                  {fld.type === "textarea" ? (
                    <textarea
                      rows={3}
                      data-testid={`field-${fld.name}`}
                      value={form[fld.name] || ""}
                      onChange={(e) => set(fld.name, e.target.value)}
                      placeholder={fld.placeholder}
                      className={inputCls}
                    />
                  ) : fld.type === "select" ? (
                    <select
                      data-testid={`field-${fld.name}`}
                      value={form[fld.name] || ""}
                      onChange={(e) => set(fld.name, e.target.value)}
                      className={inputCls}
                    >
                      <option value="">Choisir…</option>
                      {fld.options.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={fld.type || "text"}
                      data-testid={`field-${fld.name}`}
                      value={form[fld.name] || ""}
                      onChange={(e) => set(fld.name, e.target.value)}
                      placeholder={fld.placeholder}
                      className={inputCls}
                    />
                  )}
                </label>
              ))}

              <button
                type="submit"
                disabled={saving}
                data-testid="lead-modal-submit"
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: accent }}
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                {saving ? "Envoi…" : config.submitLabel || "Envoyer"}
              </button>

              <div className="flex items-center gap-3 py-1">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">ou</span>
                <span className="h-px flex-1 bg-border" />
              </div>

              <button
                type="button"
                onClick={sendWhatsApp}
                data-testid="lead-modal-whatsapp"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 px-6 py-3.5 text-sm font-semibold transition-colors hover:bg-[#25D366]/10"
                style={{ borderColor: "#25D366", color: "#128C4B" }}
              >
                <MessageCircle size={17} />
                Envoyer par WhatsApp
              </button>
              <p className="text-center text-xs text-muted-foreground">
                Ouvre WhatsApp avec votre demande pré-remplie.
              </p>
            </form>
          </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
